import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { pipe } from 'fp-ts/lib/function';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next/pages';

import { LangUri } from '@catawiki/runtime-toolkit';

import { FetchAllAuctionsParams, fetchAllAuctions } from '@/shared/api/buyer';
import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';
import { nullableData } from '@/shared/utils/http';
import { isInBuilder } from '@/shared/utils/isInBuilder';
import { TRACER_NAME_BUILDER, getSpan } from '@/shared/utils/tracer';

import { ActionVariant, BaseAction } from '../Action';
import { mapRemoteAuctionToCard } from '../Auctions/auctionCard';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import sharedStyle from '../shared.module.scss';
import { Auction, AuctionInput, Row } from '../Auctions/Row';

export const DEFAULT_ITEMS_PER_PAGE = 10;

export enum AllAuctionsSort {
  TIME_REMAINING = 'close_at',
  RECENTLY_ADDED = 'id_desc',
}

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  title?: string;
  description?: string;
  action?: BaseAction;
  actionVariant?: ActionVariant;
  showViewAll?: boolean;
  categoryId?: number;
  includeDescendants?: boolean;
  liveStream?: boolean;
  sortBy?: AllAuctionsSort;
  itemsPerPage?: number;
}

type Props = BuilderInput & {
  locale?: string;
  auctionIds?: AuctionInput[];
  auctions?: Auction[];
};

const EMPTY_AUCTIONS: Auction[] = [];
const EMPTY_AUCTION_IDS: AuctionInput[] = [];
const VIEW_ALL_ROUTE = '/a';

const buildViewAllActionUrl = (
  locale: string,
  {
    categoryId,
    liveStream = false,
  }: Pick<BuilderInput, 'categoryId' | 'liveStream'>
) => {
  const localeUri = LangUri.LangUri.of(`https://www.catawiki.com/${locale}/`);
  const basePath = localeUri.generatePath(VIEW_ALL_ROUTE);
  const searchParams = new URLSearchParams();

  if (categoryId) {
    searchParams.set('category_ids', String(categoryId));
  }

  if (liveStream) {
    searchParams.set('live', 'true');
  }

  const query = searchParams.toString();

  return query ? `${basePath}?${query}` : basePath;
};

const getLoadingAuctionIds = (itemsPerPage: number): AuctionInput[] =>
  Array.from({ length: itemsPerPage }, (_, index) => ({
    auctionId: index + 1,
  }));

const buildFetchParams = (
  locale: string,
  {
    categoryId,
    includeDescendants = false,
    liveStream = false,
    sortBy = AllAuctionsSort.TIME_REMAINING,
    itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  }: Pick<
    BuilderInput,
    | 'categoryId'
    | 'includeDescendants'
    | 'liveStream'
    | 'sortBy'
    | 'itemsPerPage'
  >
) =>
  ({
    locale,
    ...(categoryId ? { category_ids: [categoryId] } : {}),
    ...(categoryId && includeDescendants
      ? { with_descendants: true as const }
      : {}),
    ...(liveStream ? { live_only: true as const } : {}),
    status: 'not_closed' as const,
    sort: sortBy,
    page: 1,
    per_page: itemsPerPage,
  }) satisfies FetchAllAuctionsParams;

const fetchAuctionCards = async (
  locale: string,
  options: Pick<
    BuilderInput,
    | 'categoryId'
    | 'includeDescendants'
    | 'liveStream'
    | 'sortBy'
    | 'itemsPerPage'
  >
) => {
  const itemsPerPage = options.itemsPerPage || DEFAULT_ITEMS_PER_PAGE;
  const loadingAuctionIds = getLoadingAuctionIds(itemsPerPage);
  const eitherAuctions = await fetchAllAuctions(
    buildFetchParams(locale, options)
  );
  const remoteAuctions = pipe(eitherAuctions, nullableData)?.auctions || [];

  return {
    auctions: remoteAuctions.map(mapRemoteAuctionToCard),
    auctionIds: remoteAuctions.length > 0 ? [] : loadingAuctionIds,
  };
};

export const AllAuctions: FC<Props> = ({
  componentLinkId,
  title,
  description,
  action,
  actionVariant = ActionVariant.NONE,
  showViewAll = false,
  categoryId,
  includeDescendants = false,
  liveStream = false,
  sortBy = AllAuctionsSort.TIME_REMAINING,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  auctionIds = EMPTY_AUCTION_IDS,
  auctions = EMPTY_AUCTIONS,
  backgroundColorClassName,
  builderBlock,
  builderState,
  locale,
  attributes,
}) => {
  const [clientAuctions, setClientAuctions] = useState(auctions);
  const [clientAuctionIds, setClientAuctionIds] = useState(auctionIds);
  const { t } = useTranslation();
  const { key: _builderKey, ...domAttributes } = attributes || {};
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);
  const insideBuilder = isInBuilder();

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale || 'en';

  useEffect(() => {
    if (!insideBuilder) {
      setClientAuctions(auctions);
      setClientAuctionIds(auctionIds);
      return undefined;
    }

    if (auctions.length > 0 || auctionIds.length > 0) {
      setClientAuctions(auctions);
      setClientAuctionIds(auctionIds);
      return undefined;
    }

    let cancelled = false;

    setClientAuctions([]);
    setClientAuctionIds(getLoadingAuctionIds(itemsPerPage));

    const loadAuctions = async () => {
      const result = await fetchAuctionCards(blockLocale, {
        categoryId,
        includeDescendants,
        liveStream,
        sortBy,
        itemsPerPage,
      });

      if (cancelled) return;

      setClientAuctions(result.auctions);
      setClientAuctionIds(result.auctionIds);
    };

    loadAuctions();

    return () => {
      cancelled = true;
    };
  }, [
    insideBuilder,
    auctions,
    auctionIds,
    blockLocale,
    categoryId,
    includeDescendants,
    liveStream,
    sortBy,
    itemsPerPage,
  ]);

  const resolvedAuctions = insideBuilder ? clientAuctions : auctions;
  const resolvedAuctionIds = insideBuilder ? clientAuctionIds : auctionIds;
  const resolvedAction = showViewAll
    ? {
        label: t('view_all_followed_auctions'),
        url: buildViewAllActionUrl(blockLocale, {
          categoryId,
          liveStream,
        }),
      }
    : action;
  const resolvedActionVariant = showViewAll
    ? ActionVariant.TERTIARY
    : actionVariant;

  return (
    <div
      {...domAttributes}
      id={componentLinkId}
      data-testid="all-auctions"
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <ComponentHeader
        title={title}
        description={description}
        action={resolvedAction}
        actionVariant={resolvedActionVariant}
        locale={blockLocale}
        headerClassName={cn('u-wrapper', {
          'u-m-b-xl u-m-b-lg-xxl-6': resolvedAuctions.length > 0,
        })}
        mobileActionClassName={'u-wrapper'}
      >
        <Row
          locale={blockLocale}
          auctionIds={resolvedAuctionIds}
          auctions={resolvedAuctions}
        />
      </ComponentHeader>
    </div>
  );
};

export function dataFetcher(locale: string) {
  return async ({
    categoryId,
    includeDescendants = false,
    liveStream = false,
    sortBy = AllAuctionsSort.TIME_REMAINING,
    itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  }: BuilderInput) => {
    const span = getSpan(TRACER_NAME_BUILDER, 'AllAuctions.dataFetcher');
    const { auctions, auctionIds } = await fetchAuctionCards(locale, {
      categoryId,
      includeDescendants,
      liveStream,
      sortBy,
      itemsPerPage,
    });

    span.end();

    return {
      locale,
      auctionIds,
      auctions,
    };
  };
}

export const NAME = 'AllAuctions';

export function componentRegister() {
  const allAuctionActionInputs = actionButtonInput({
    allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
    useCatawikiPlainEditor: false,
  }).map((input) => ({
    ...input,
    showIf:
      input.name === 'action'
        ? `!options.get('showViewAll') && options.get('actionVariant') !== 'none'`
        : `!options.get('showViewAll')`,
  }));

  Builder.registerComponent(AllAuctions, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'html',
        defaultValue:
          'Section <span class="u-typography-h2-serif u-color-brand">Title</span>',
      },
      {
        name: 'description',
        type: 'html',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...allAuctionActionInputs,
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'categoryId',
        friendlyName: 'Category ID',
        type: 'number',
      },
      {
        name: 'includeDescendants',
        friendlyName: 'Include descendant categories',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'liveStream',
        friendlyName: 'Livestream only',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'showViewAll',
        friendlyName: 'Show view all',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'sortBy',
        friendlyName: 'Sort by',
        type: 'string',
        defaultValue: AllAuctionsSort.TIME_REMAINING,
        enum: [
          {
            label: 'Time remaining',
            value: AllAuctionsSort.TIME_REMAINING,
          },
          {
            label: 'Recently added',
            value: AllAuctionsSort.RECENTLY_ADDED,
          },
        ],
      },
      {
        name: 'itemsPerPage',
        friendlyName: 'Number of auctions',
        type: 'number',
        defaultValue: DEFAULT_ITEMS_PER_PAGE,
      },
    ],
    noWrap: true,
  });

  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
});

export { buildViewAllActionUrl };
